// src/components/updateNotifications/UserUpdateNotificationModal.tsx

import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Typography, Divider } from "antd";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";
import { UserUpdateNotification} from "../../types/communications/updateNotifications";
import { markUpdateNotificationAsRead, dismissUpdateNotification } from "../../api/updateNotifications/updateNotificationsApi";
import { useNotifications } from "../../context/NotificationContext";

const { Title, Text } = Typography;

interface UserUpdateNotificationModalProps {
	notification: UserUpdateNotification | null;
	open: boolean;
	onClose: () => void;
	onDismiss: () => void;
}

const UserUpdateNotificationModal: React.FC<UserUpdateNotificationModalProps> = ({ notification, open, onClose, onDismiss }) => {
	const [currentPage, setCurrentPage] = useState(0);
	const [canClose, setCanClose] = useState(false);
	const [countdown, setCountdown] = useState(5);
	const { showWarning } = useNotifications();

	useEffect(() => {
		if (open && notification && !notification.read_at) {
			markUpdateNotificationAsRead(notification.id).catch(console.error);
		}
	}, [open, notification]);

	useEffect(() => {
		if (open) {
			setCurrentPage(0);
			setCanClose(false);
			setCountdown(5);

			// Start countdown timer
			const timer = setInterval(() => {
				setCountdown((prev) => {
					if (prev <= 1) {
						setCanClose(true);
						clearInterval(timer);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(timer);
		}
	}, [open]);

	const handleDismiss = async () => {
		if (!canClose) {
			showWarning(`Please wait ${countdown} seconds before dismissing`);
			return;
		}
		if (!notification) return;
		try {
			await dismissUpdateNotification(notification.id);
			onDismiss();
		} catch (error) {
			console.error("Failed to dismiss notification");
		}
	};

	const handleClose = () => {
		if (!canClose) {
			showWarning(`Please wait ${countdown} seconds before closing`);
			return;
		}
		onClose();
	};

	if (!notification) return null;

	const pages = notification.pages || [];
	const media = notification.media || [];

	return (
		<Modal
			title={
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Title level={4} style={{ margin: 0 }}>
						{notification.title}
					</Title>
					<Button type="text" icon={<CloseOutlined />} onClick={handleDismiss} size="small" disabled={!canClose} />
				</div>
			}
			open={open}
			onCancel={handleClose}
			footer={null}
			width={700}
			closable={false}
			maskClosable={canClose}
		>
			<div>
				{media.length > 0 && (
					<div style={{ marginBottom: 16 }}>
						{media.map((m) => (
							<div key={m.file_id} style={{ marginBottom: 8 }}>
								{m.file_type === "image" ? (
									<img src={m.file_url} alt={m.file_name} style={{ maxWidth: "100%", borderRadius: 8 }} />
								) : (
									<video controls style={{ maxWidth: "100%", borderRadius: 8 }}>
										<source src={m.file_url} />
									</video>
								)}
							</div>
						))}
					</div>
				)}

				<div style={{ marginBottom: 16 }}>
					{pages[currentPage]?.title && <Title level={5}>{pages[currentPage].title}</Title>}
					<div dangerouslySetInnerHTML={{ __html: pages[currentPage]?.content || "" }} />
				</div>

				{pages.length > 1 && (
					<>
						<Divider />
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Button disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)}>
								Previous
							</Button>
							<Text>
								Page {currentPage + 1} of {pages.length}
							</Text>
							<Button disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(currentPage + 1)}>
								Next
							</Button>
						</div>
					</>
				)}

				<Divider />

				<div style={{ textAlign: "right" }}>
					<Space>
						<Button onClick={handleClose} disabled={!canClose}>
							{canClose ? "Close" : `Close (${countdown}s)`}
						</Button>
						<Button type="primary" icon={<CheckOutlined />} onClick={handleDismiss} disabled={!canClose}>
							{canClose ? "Got it" : `Got it (${countdown}s)`}
						</Button>
					</Space>
				</div>
			</div>
		</Modal>
	);
};

export default UserUpdateNotificationModal;
